import { useState, useEffect, useCallback } from "react";
import { Outlet, NavLink as RouterNavLink, useLocation } from "react-router-dom";
import {
  AppShell,
  NavLink,
  Group,
  Text,
  ActionIcon,
  Select,
  Avatar,
  Divider,
  useMantineColorScheme,
  useComputedColorScheme,
  Box,
  Tooltip,
} from "@mantine/core";
import {
  IconDashboard,
  IconPhoto,
  IconDatabase,
  IconClipboardCheck,
  IconFolder,
  IconTags,
  IconCategory,
  IconKey,
  IconUsers,
  IconSun,
  IconMoon,
  IconLogout,
} from "@tabler/icons-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const CORE_NAV = [
  { label: "Хянах самбар", icon: IconDashboard, path: "/" },
  { label: "Зурагнууд", icon: IconPhoto, path: "/image", roles: ["maintainer", "developer", "guest"] },
];

const DATA_NAV = [
  { label: "Датасет", icon: IconDatabase, path: "/dataset", roles: ["maintainer"] },
  { label: "Аудит", icon: IconClipboardCheck, path: "/audit", roles: ["maintainer", "developer"] },
];

const SYSTEM_NAV = [
  { label: "Төслүүд", icon: IconFolder, path: "/settings/project", roles: ["maintainer"] },
  { label: "Таних нэрс", icon: IconTags, path: "/settings/classname", roles: ["maintainer"] },
  { label: "Зургийн төрөл", icon: IconCategory, path: "/settings/image-type", roles: ["maintainer"] },
  { label: "API Түлхүүр", icon: IconKey, path: "/settings/api-key", roles: ["maintainer"] },
  { label: "Хэрэглэгчид", icon: IconUsers, path: "/settings/user", roles: ["maintainer"] },
];

export default function AdminLayout() {
  const { user, logout, hasRole, setUser } = useAuth();
  const location = useLocation();
  const { setColorScheme } = useMantineColorScheme();
  const colorScheme = useComputedColorScheme("light");
  const isDark = colorScheme === "dark";

  const [projects, setProjects] = useState<any[]>([]);

  const loadProjects = useCallback(async () => {
    try {
      const res = await api.project.select();
      setProjects(res.data);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const handleProjectChange = async (val: string | null) => {
    if (!val) return;
    try {
      const res = await api.project.change(val);
      if (user) {
        setUser({ ...user, project: res.data });
      }
      // Navigate to dashboard and reload data
      window.location.href = "/";
    } catch {
      // ignore
    }
  };

  const isActive = (path: string) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  const filterNav = (items: typeof CORE_NAV) =>
    items.filter((item) => !("roles" in item) || !item.roles || hasRole(...item.roles));

  const renderNavItems = (items: typeof CORE_NAV) =>
    filterNav(items).map((item) => (
      <NavLink
        key={item.path}
        component={RouterNavLink}
        to={item.path}
        label={item.label}
        leftSection={<item.icon size={18} stroke={1.5} />}
        active={isActive(item.path)}
        style={{
          borderRadius: 8,
          marginBottom: 2,
          fontSize: "13.5px",
          fontWeight: 500,
        }}
      />
    ));

  const initials = user?.nickname ? user.nickname.charAt(0).toUpperCase() : "?";

  return (
    <AppShell
      navbar={{ width: 260, breakpoint: "sm" }}
      padding="lg"
    >
      <AppShell.Navbar
        p="sm"
        style={{
          borderRight: `1px solid var(--app-border)`,
          backgroundColor: "var(--app-surface)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Logo */}
        <AppShell.Section>
          <Group justify="space-between" px="xs" py="xs" mb="xs">
            <img
              src={isDark ? "/logo-light.svg" : "/logo-dark.svg"}
              alt="YOLO Dataset"
              height={28}
            />
            <ActionIcon
              variant="subtle"
              size="sm"
              color={isDark ? "yellow" : "blue"}
              onClick={() => setColorScheme(isDark ? "light" : "dark")}
            >
              {isDark ? <IconSun size={16} /> : <IconMoon size={16} />}
            </ActionIcon>
          </Group>
        </AppShell.Section>

        {/* Project Selector */}
        {projects.length > 0 && (
          <AppShell.Section px="xs" mb="sm">
            <Select
              size="xs"
              placeholder="Төсөл сонгох"
              data={projects.map((p: any) => ({ value: p._id, label: p.name }))}
              value={user?.project?._id || null}
              onChange={handleProjectChange}
              styles={{
                input: {
                  borderColor: "var(--app-border)",
                  fontSize: "12.5px",
                },
              }}
            />
          </AppShell.Section>
        )}

        <Divider mb="xs" color="var(--app-border-light)" />

        {/* Navigation */}
        <AppShell.Section grow style={{ overflowY: "auto" }} px={4}>
          <Text size="10px" fw={700} c="dimmed" tt="uppercase" px="sm" mb={4}>
            Үндсэн
          </Text>
          {renderNavItems(CORE_NAV)}

          <Text size="10px" fw={700} c="dimmed" tt="uppercase" px="sm" mt="md" mb={4}>
            Өгөгдөл
          </Text>
          {renderNavItems(DATA_NAV)}

          <Text size="10px" fw={700} c="dimmed" tt="uppercase" px="sm" mt="md" mb={4}>
            Систем
          </Text>
          {renderNavItems(SYSTEM_NAV)}
        </AppShell.Section>

        <Divider mt="xs" color="var(--app-border-light)" />

        {/* User Profile */}
        <AppShell.Section px="xs" py="sm">
          <Group justify="space-between">
            <Group gap="sm">
              <Avatar size={34} radius="xl" color="dark" variant="filled">
                {initials}
              </Avatar>
              <div>
                <Text size="13px" fw={600} lh={1.3}>
                  {user?.nickname}
                </Text>
                <Text size="11px" c="dimmed" lh={1.2}>
                  {user?.role}
                </Text>
              </div>
            </Group>
            <Tooltip label="Гарах" position="top">
              <ActionIcon variant="subtle" size="sm" color="red" onClick={logout}>
                <IconLogout size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </AppShell.Section>
      </AppShell.Navbar>

      <AppShell.Main style={{ backgroundColor: "var(--app-surface-dim)" }}>
        <Box maw={1400} mx="auto">
          <Outlet />
        </Box>
      </AppShell.Main>
    </AppShell>
  );
}
